<template>
  <div data-module="four-oh-four">
    <div class="module-layout">

      <!-- container for all image content -->
      <div class="image-container"
        :class="imageContainerClasses"
        ref="imageContainer"
      >
        <div class="image" :style="imageStyles" />
      </div>

      <!-- container for text content -->
      <div class="content-container"
        :class="containerClasses"
      >
        <h1 v-if="shouldShowTitle" v-html="contentTitle" />
        <markdown class="markdown">{{contentBody}}</markdown>
      </div>
    </div>
  </div>
</template>


<script>
/*
  Work section ImageTextColumn module

  data record:
  {
    imageSrc: <url>,
    aspectRatio: <string> // '16:9', '4:3', '1:1'
    showTitle: <boolean> // true, false
  }
*/
// Libs
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';

// Constants
import FoF from '@/data/four-oh-four';

import WorkMixin from './work/WorkMixin';


export default {

  name: 'four-oh-four',

  mixins: [WorkMixin],

  props: ['data'],

  data() {
    return {
      imageContentHeight: 0,
      imageContentWidth: 0,
    };
  },

  computed: {

    contentBody() {
      return FoF.body;
    },

    contentTitle() {
      return FoF.title;
    },

    containerClasses() {
      // return class string, eg 'desktop', 'mobile'
      let deviceStr = '';
      if (this.isMobile) {
        deviceStr = 'mobile';
      } else if (this.isTablet) {
        deviceStr = 'tablet';
      } else {
        deviceStr = 'desktop';
      }
      return deviceStr;
    },

    imageStyles() {
      const { imageContentHeight, imageContentWidth, data } = this;
      const { imageSrc } = data;

      const maxDim = Math.max(imageContentWidth, imageContentHeight);
      const rUrl = getResponsiveUrl(imageSrc, maxDim);

      const styles = {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `center center`,
      };

      return styles;
    },

    imageContainerClasses() {
      const classStr = this.containerClasses;
      const { aspectRatio } = this.data;

      let aspectStr = '';
      switch (aspectRatio) {
        case '16:9':
          aspectStr = 'aspect16-9';
          break;
        case '4:3':
          aspectStr = 'aspect4-3';
          break;
        case '1:1':
        default:
          aspectStr = 'aspect1-1';
          break;
      }

      return `${classStr} ${aspectStr}`;
    },

    shouldShowTitle() {
      const { showTitle } = this.data;
      return (showTitle === true);
    },

  },

  methods: {

    measureLayout() {
      this.imageContentHeight = this.getImageContentHeight();
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentHeight() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentWidth() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },

};
</script>


<style lang="scss" scoped>
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';
@import '~@/styles/_typography.scss';

$layout--percentage: $layout--max-content-width-ng / $layout--optimal-desktop * 100%;

[data-module="four-oh-four"] {
  width: 100%;

  // media query props
  margin-top: rem($height--nav-bar-mobile * 1px); // adjust for nav bar
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-top: rem($height--nav-bar-mobile * 1px);
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-top: rem($height--nav-bar-desktop * 1px);
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }


  .module-layout {
    // media query props
    @include ngGrid('mobile');
    padding-top: rem(28px); // whitespace between nav / image

    @include ngMq($nbp--tablet, up) {
      @include ngGrid('tablet');
      padding-top: rem(32px);
    }

    @include ngMq($nbp--desktop, up) {
      @include ngGrid('desktop');
      padding-top: rem(65px);
    }

    /*
      image container
    */

    .image-container {

      // media query props
      margin-bottom: rem((30 - $grid-gap-mobile) * 1px); // margin image/title

      @include ngMq($nbp--tablet, up) {
        margin-bottom: rem((30 - $grid-gap-tablet) * 1px);
      }
      @include ngMq($nbp--desktop, up) {
        margin-bottom: rem((42 - $grid-gap-desktop) * 1px);
      }

      .image {
        /*
          on image style attrs:
          background-image
          background-position
        */
        overflow: hidden;
        background-size: contain;
        background-repeat: no-repeat;

        width: 100%;
      }

      .caption {
        margin-top: rem(10px);
        padding: 0 rem(20px);
      }
    }

    // image mobile

    .image-container.mobile {
      grid-column: 1 / -1;
      grid-row: 1;

      display: block;
    }

    .image-container.mobile.aspect16-9 .image {
      padding-bottom: $dimension-16-9;
    }
    .image-container.mobile.aspect4-3 .image {
      padding-bottom: $dimension-4-3;
    }
    .image-container.mobile.aspect1-1 .image {
      padding-bottom: $dimension-1-1;
    }

    // image tablet

    .image-container.tablet {
      grid-row: 1;
    }

    .image-container.tablet.aspect16-9 {
      grid-column: 1 / span 8;
      .image {
        padding-bottom: $dimension-16-9;
      }
    }
    .image-container.tablet.aspect4-3 {
      grid-column: 1 / span 7;
      .image {
        padding-bottom: $dimension-4-3;
      }
    }
    .image-container.tablet.aspect1-1 {
      grid-column: 1 / span 5;
      .image {
        padding-bottom: $dimension-1-1;
      }
    }

    // image desktop

    .image-container.desktop {
      grid-row: 1;
    }

    .image-container.desktop.aspect16-9 {
      grid-column: 1 / span 8;
      .image {
        padding-bottom: $dimension-16-9;
      }
    }
    .image-container.desktop.aspect4-3 {
      grid-column: 1 / span 7;
      .image {
        padding-bottom: $dimension-4-3;
      }
    }
    .image-container.desktop.aspect1-1 {
      grid-column: 1 / span 7;
      .image {
        padding-bottom: $dimension-1-1;
      }
    }

    /*
      content container
    */

    .content-container {
      text-align: left;

      h1 {

        // media query props
        @include serif( $size: (30, 30, 0) );
        margin-top: rem((30 - 30) * 0.5px); // adjust for line height
        margin-bottom: rem(26px); // margin title/body

        @include ngMq($nbp--tablet, up) {
          @include serif( $size: (33, 63, 0) );
          margin-top: rem((33 - 63) * 0.5px); // adjust for line height
          margin-bottom: rem(12px);
        }

        @include ngMq($nbp--desktop, up) {
          @include serif( $size: (48, 63, 0) );
          margin-top: rem((48 - 63) * 0.5px); // adjust for line height
          margin-bottom: rem(40px);
        }
      }

      .markdown {
        margin-top: rem(-8px);

        p {
          // TODO: why not working ?
          margin-top: rem(6px);
        }
      }
    }

    // content mobile

    .content-container.mobile {
      margin-right: 0;
      margin-left: 0;

      grid-column: 1 / -1;
      grid-row: 2;
    }

    // content tablet

    .content-container.tablet {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    // content desktop

    .content-container.desktop {
      grid-column: 1 / span 9;
      grid-row: 2;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/FourOhFour.vue